import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import trash from "../../../../../../assets/images/trash.svg";
import eye from "../../../../../../assets/images/eye.svg";
import downloadIcon from "../../../../../../assets/images/downloadIcon.svg";
import { useCaseStore } from "../../../../../../store/useCaseStore";
import dayjs from "dayjs";
import {
  deleteDocument,
  getBodyRemovalDetails,
  getCaseDetails,
  getDocument,
} from "../../../../../../services/dashboard";
import ConfirmDeletePopup from "../../../../../../components/ConfirmDeletePopup";
import Loader from "../../../../../../components/Loader";
import VerifiedPopup from "../../../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../../../store/useVerifiedStore";
import ButtonComp from "../../../../../../components/Button";
import Input from "../../../../../../components/Input";
import { uploadDocuments } from "../../../../../../services/case-services";
import { useToast } from "../../../../../../hooks/useToast";
import { useBodyRemovalsStore } from "../../../../../../store/useBodyRemovalsStore";
import { useCaseCreationStore } from "../../../../../../store/useCaseCreationStore";

interface DocumentRow {
  id: number;
  documentName: string;
  documentType: string;
  uploadedBy: string;
  date: string;
  file: File;
}

export default function Documents({ documentData, uniqueId, viewMode }: any) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState("");

  const { selectedCase, setSelectedCase } = useCaseStore();

  const { selectedBodyRemoval, setSelectedBodyRemoval } =
    useBodyRemovalsStore();

  const { showVerified, setShowVerified, verifiedLabel, setVerifiedLabel } =
    useVerifiedStore();
  const { showInfoToast } = useToast();
  const { setDocuments } = useCaseCreationStore();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const documentsList = documentData || [];
  const claimNumber = selectedCase?.isManual
    ? selectedCase?.caseNumber
    : selectedCase?.iceClaimNumber;

  const getStoreDocumentKey = (type: string) => {
    const mapping: Record<string, string> = {
      CLAIMANT_ID: "claimant_id",
      DECEASED_ID: "deceased_id",
      DHA_1663_FORM: "dha_1663_form",
      DHA_1680_HOME_DEATH_FORM: "dha_1680_home_death_form",
      DEATH_CERTIFICATE: "death_certificate",
      OTHER_DOCUMENT: "other_document",
      BANK_STATEMENT: "bank_statement",
      SIGNATURE_OF_CLAIMANT: "signature_of_claimant",
    };

    return mapping[type];
  };

  const handleAdd = async () => {
    if (!selectedFile || !documentType || !uniqueId) {
      showInfoToast("Please select file and document type");
      return;
    }

    try {
      setIsLoading(true);

      const formData = new FormData();

      formData.append("caseId", uniqueId);
      formData.append(documentType, selectedFile);

      const response = await uploadDocuments(formData);

      if (response?.status === 200) {
        const storeDocumentKey = getStoreDocumentKey(documentType);
        if (storeDocumentKey) {
          setDocuments({
            [storeDocumentKey]: selectedFile,
          });
        }
        await refreshCase();
        setVerifiedLabel("Uploaded");
        setShowVerified(true);

        setSelectedFile(null);
        setDocumentType("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async (documentId: any) => {
    const response = await getDocument(claimNumber, documentId);
    if (response.status === 200) {
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setSelectedDoc({ file_url: url });
      setOpenPreview(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocId || !claimNumber) return;

    try {
      setIsLoading(true);
      const selectedDocument = documentsList.find(
        (document: any) => document.document_id === selectedDocId,
      );
      const response = await deleteDocument(claimNumber, selectedDocId);

      if (response.status === 200) {
        const storeDocumentKey = getStoreDocumentKey(
          selectedDocument?.document_type || "",
        );
        if (storeDocumentKey) {
          setDocuments({
            [storeDocumentKey]: null,
          });
        }
        await refreshCase();
        setShowConfirmDelete(false);
        setSelectedDocId(null);
        setVerifiedLabel("Deleted");
        setShowVerified(true);
      }
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setIsLoading(false); // ✅ always stop loader
    }
  };

  const handleDownload = async (documentId: any, fileName: any) => {
    try {
      const res = await getDocument(claimNumber, documentId);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const handleCloseConfirmPopup = () => {
    setShowConfirmDelete(false);
  };
  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const refreshCase = async () => {
    if (viewMode === "BODY_REMOVAL") {
      if (!selectedBodyRemoval?.bodyRemovalNumber) return;

      const res = await getBodyRemovalDetails(
        selectedBodyRemoval.bodyRemovalNumber,
      );
      if (res.status === 200) {
        setSelectedBodyRemoval(res.data);
      }
      return;
    }

    if (!selectedCase?.caseNumber) return;

    const res = await getCaseDetails(selectedCase.caseNumber);
    if (res.status === 200) {
      setSelectedCase(res.data);
    }
  };

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showVerified]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} alignItems="center">
        {/* Document Type */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography
            sx={{ mb: 1, fontSize: 18, fontWeight: 600, textAlign: "left" }}
          >
            Document Type
          </Typography>

          <Input
            select
            fullWidth
            value={documentType}
            onChange={(e: any) => setDocumentType(e.target.value)}
          >
            <MenuItem value="">Select Document Type</MenuItem>
            <MenuItem value="DECEASED_ID">Deceased ID</MenuItem>
            <MenuItem value="CLAIMANT_ID">Claimant ID </MenuItem>
            <MenuItem value="DEATH_CERTIFICATE">Death Certificate</MenuItem>
            <MenuItem value="DHA_1663_FORM">DHA 1663</MenuItem>
            <MenuItem value="DHA_1680_HOME_DEATH_FORM">DHA 1680</MenuItem>
            <MenuItem value="BANK_STATEMENT">Bank Statement</MenuItem>
          </Input>
        </Grid>

        {/* File Upload */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography
            sx={{ mb: 1, fontSize: 18, fontWeight: 600, textAlign: "left" }}
          >
            Document
          </Typography>

          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              height: 56,
              display: "flex",
              alignItems: "center",
              px: 2,
            }}
          >
            <ButtonComp
              variant="contained"
              sx={{
                backgroundColor: "#6D6D6D",
                borderRadius: "12px",
                height: 36,
                minWidth: 100,
                mr: 2,
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose
            </ButtonComp>

            <Typography sx={{ color: "#6B7280" }}>
              {selectedFile ? selectedFile.name : ""}
            </Typography>

            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={(e) =>
                setSelectedFile(e.target.files ? e.target.files[0] : null)
              }
            />
          </Box>
        </Grid>

        {/* ADD Button */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Box mt={{ xs: 2, md: 4 }}>
            <ButtonComp
              variant="contained"
              onClick={handleAdd}
              sx={{
                backgroundColor: "#EF6C00",
                borderRadius: "30px",
                height: 56,
                width: "100%",
                fontWeight: 600,
                fontSize: 16,
                "&:hover": {
                  backgroundColor: "#E65100",
                },
              }}
            >
              ADD
            </ButtonComp>
          </Box>
        </Grid>
      </Grid>
      {/* Table Wrapper */}
      {isLoading && <Loader isLoading={isLoading} />}
      {documentsList.length > 0 ? (
        <Box sx={{ border: "1px solid #D8DDDA", mt: 4 }}>
          <Table>
            {/* Table Header */}
            <TableHead sx={{ backgroundColor: "background.paper" }}>
              <TableRow>
                <TableCell sx={headerCellStyle}>Document Name</TableCell>
                <TableCell sx={headerCellStyle}>Document Type</TableCell>
                <TableCell sx={headerCellStyle}>Uploaded By</TableCell>
                <TableCell sx={headerCellStyle}>Date</TableCell>
                <TableCell sx={headerCellStyle}>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {documentsList?.map((row: any, index: any) => (
                <TableRow key={index}>
                  <TableCell sx={bodyCellStyle}>{row.file_name}</TableCell>
                  <TableCell sx={{ ...bodyCellStyle, color: "#6b6b6b" }}>
                    {row.document_type}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>{row.created_by}</TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {dayjs(row.created_on).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <IconButton size="small">
                      <img
                        src={eye}
                        alt="eye"
                        onClick={() => handlePreview(row.document_id)}
                      />
                    </IconButton>
                    <IconButton>
                      <img
                        src={trash}
                        alt="trash"
                        onClick={() => {
                          setSelectedDocId(row.document_id);
                          setShowConfirmDelete(true);
                        }}
                      />
                    </IconButton>
                    <IconButton>
                      <img
                        src={downloadIcon}
                        alt="trash"
                        onClick={() =>
                          handleDownload(row.document_id, row.file_name)
                        }
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography sx={{ padding: 4 }}>No Documents to display</Typography>
      )}
      <ConfirmDeletePopup
        open={showConfirmDelete}
        onClose={handleCloseConfirmPopup}
        confirmDelete={handleConfirmDelete}
      />
      <VerifiedPopup
        label={verifiedLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const headerCellStyle = {
  fontWeight: 600,
  fontSize: "14px",
  padding: "12px 12px",
  color: "#1f1f1f",
  borderBottom: "1px solid #d9d9d9",
};

const bodyCellStyle = {
  fontSize: "14px",
  //padding: "24px 32px",
  borderBottom: "1px solid #d9d9d9",
};
